import { useCallback, useEffect, useRef, useState } from 'react'
import type { User } from '@w/auth'
import type { AuthClient } from './auth-client'

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

let googleScriptPromise: Promise<void> | null = null

export interface GoogleCredentialResponse {
  credential?: string
}

interface GoogleIdApi {
  initialize(config: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    use_fedcm_for_prompt?: boolean
    use_fedcm_for_button?: boolean
    cancel_on_tap_outside?: boolean
  }): void
  renderButton(parent: HTMLElement, options: GoogleButtonOptions): void
  prompt(): void
}

export type GoogleButtonOptions = {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'large' | 'medium' | 'small'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  logo_alignment?: 'left' | 'center'
  width?: number
  locale?: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdApi
      }
    }
  }
}

export type UseGoogleOneTapSignInConfig<AuthUser = User> = {
  authClient: AuthClient<AuthUser>
  clientId: string
  enabled?: boolean
  autoPrompt?: boolean
  testIdToken?: string
  useFedcmForPrompt?: boolean
  cancelOnTapOutside?: boolean
  missingCredentialMessage?: string
  loadFailedMessage?: string
  onSuccess?: (user: AuthUser) => void
  onError?: (error: Error) => void
}

export type UseGoogleOneTapSignInResult = {
  isLoading: boolean
  isReady: boolean
  error: Error | null
  prompt: () => void
  renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void
  signInWithCredential: (response: GoogleCredentialResponse) => Promise<void>
}

export function useGoogleOneTapSignIn<AuthUser>({
  authClient,
  clientId,
  enabled = true,
  autoPrompt = true,
  testIdToken,
  useFedcmForPrompt = true,
  cancelOnTapOutside = false,
  missingCredentialMessage = 'Missing Google credential',
  loadFailedMessage = 'Failed to load Google sign-in',
  onSuccess,
  onError,
}: UseGoogleOneTapSignInConfig<AuthUser>): UseGoogleOneTapSignInResult {
  const readyRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fail = useCallback(
    (nextError: Error) => {
      setError(nextError)
      onError?.(nextError)
    },
    [onError],
  )

  const signInWithCredential = useCallback(
    async ({ credential }: GoogleCredentialResponse) => {
      if (!credential) {
        fail(new Error(missingCredentialMessage))
        return
      }

      setError(null)
      setIsLoading(true)

      try {
        const user = await authClient.signIn.googleIdToken({
          idToken: credential,
        })
        onSuccess?.(user)
      } catch (caught) {
        fail(caught instanceof Error ? caught : new Error(String(caught)))
      } finally {
        setIsLoading(false)
      }
    },
    [authClient, fail, missingCredentialMessage, onSuccess],
  )

  const prompt = useCallback(() => {
    if (testIdToken) {
      void signInWithCredential({ credential: testIdToken })
      return
    }

    const googleId = window.google?.accounts.id
    if (!googleId || !readyRef.current) {
      fail(new Error(loadFailedMessage))
      return
    }

    setError(null)
    googleId.prompt()
  }, [fail, loadFailedMessage, signInWithCredential, testIdToken])

  const renderButton = useCallback(
    (parent: HTMLElement, options: GoogleButtonOptions) => {
      const googleId = window.google?.accounts.id
      if (!googleId || !readyRef.current) {
        fail(new Error(loadFailedMessage))
        return
      }

      googleId.renderButton(parent, options)
    },
    [fail, loadFailedMessage],
  )

  useEffect(() => {
    let disposed = false

    readyRef.current = false
    // oxlint-disable-next-line react/set-state-in-effect -- reset state while synchronizing with the external Google Identity lifecycle
    setIsReady(false)

    if (!enabled) {
      setIsLoading(false)
      return () => {
        disposed = true
      }
    }

    if (testIdToken) {
      readyRef.current = true
      setIsReady(true)
      setIsLoading(false)
      return () => {
        disposed = true
        readyRef.current = false
      }
    }

    const initialize = async () => {
      setIsLoading(true)

      try {
        await loadGoogleIdentityScript()
        if (disposed) return

        const googleId = window.google?.accounts.id
        if (!googleId) {
          throw new Error(loadFailedMessage)
        }

        googleId.initialize({
          client_id: clientId,
          callback: (response) => {
            void signInWithCredential(response)
          },
          use_fedcm_for_prompt: useFedcmForPrompt,
          use_fedcm_for_button: true,
          cancel_on_tap_outside: cancelOnTapOutside,
        })
        readyRef.current = true
        setIsReady(true)
        if (autoPrompt) googleId.prompt()
      } catch (caught) {
        if (!disposed) {
          fail(caught instanceof Error ? caught : new Error(String(caught)))
        }
      } finally {
        if (!disposed) {
          setIsLoading(false)
        }
      }
    }

    void initialize()

    return () => {
      disposed = true
      readyRef.current = false
    }
  }, [
    autoPrompt,
    cancelOnTapOutside,
    clientId,
    enabled,
    fail,
    loadFailedMessage,
    signInWithCredential,
    testIdToken,
    useFedcmForPrompt,
  ])

  return {
    isLoading,
    isReady,
    error,
    prompt,
    renderButton,
    signInWithCredential,
  }
}

export function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts.id) {
    return Promise.resolve()
  }

  if (googleScriptPromise) {
    return googleScriptPromise
  }

  googleScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[src="${GOOGLE_SCRIPT_SRC}"]`,
    )

    if (existing instanceof HTMLScriptElement) {
      existing.addEventListener(
        'load',
        () => {
          resolve()
        },
        { once: true },
      )
      existing.addEventListener(
        'error',
        () => {
          reject(new Error('Failed to load Google script'))
        },
        { once: true },
      )
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener(
      'load',
      () => {
        resolve()
      },
      { once: true },
    )
    script.addEventListener(
      'error',
      () => {
        reject(new Error('Failed to load Google script'))
      },
      { once: true },
    )
    document.head.append(script)
  })

  return googleScriptPromise
}
